import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, type ExamResult as Result } from "../api.js";

/**
 * A student's own exam result.
 *
 * Marks per question and the total, and nothing else -- no test cases, no
 * expected output, no answer key. A result is not a way back into the paper,
 * which a student in a later batch would otherwise be handed.
 *
 * Whether the result may be shown at all is the server's decision
 * (`showResultsToStudent`); this page renders whatever it is given and reports
 * the refusal when there is one.
 */
export default function ExamResult() {
  const { id } = useParams<{ id: string }>();
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableAt, setAvailableAt] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .get<Result>(`/api/exams/${id}/result`)
      .then(setResult)
      .catch((e: unknown) => {
        // "Not yet" is a different thing to say than "not for you", and the
        // server distinguishes them -- so the page should too.
        if (e instanceof ApiError && typeof e.body?.availableAt === "string") {
          setAvailableAt(e.body.availableAt);
        }
        setError(e instanceof Error ? e.message : "Could not load your result.");
      });
  }, [id]);

  if (error) {
    return (
      <div className="page">
        <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
        <div className="card" style={{ marginTop: 12 }}>
          <div className="notice" style={{ marginBottom: availableAt ? 8 : 0 }}>{error}</div>
          {availableAt && (
            <div className="muted" style={{ fontSize: 13 }}>
              Check back after {new Date(availableAt).toLocaleString()}.
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!result) return <div className="page muted">Loading…</div>;

  const answered = result.questions.filter((q) => q.scored !== null).length;

  return (
    <div className="page">
      <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
      <h1 style={{ fontSize: 22, margin: "10px 0 6px" }}>{result.exam.title}</h1>
      <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
        {result.attempt.submittedAt
          ? `Submitted ${new Date(result.attempt.submittedAt).toLocaleString()}`
          : "Not submitted"}
        {result.attempt.status === "AUTO_SUBMITTED" && " · submitted automatically at the deadline"}
      </div>

      <div className="card">
        <div style={{ fontSize: 28, fontWeight: 600 }}>
          {result.attempt.finalScore ?? "—"}{" "}
          <span className="muted" style={{ fontSize: 18, fontWeight: 400 }}>
            / {result.totalMarks}
          </span>
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          {answered} of {result.questions.length} question
          {result.questions.length === 1 ? "" : "s"} answered
        </div>
      </div>

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>PER QUESTION</div>
        {result.questions.map((q, i) => (
          <div className="test-row" key={i}>
            <span className="muted">{i + 1}.</span>
            <span className="name">
              {q.title} {q.kind === "MCQ" && <span className="badge draft">multiple choice</span>}
            </span>
            {q.scored === null ? (
              <span className="badge draft">not answered</span>
            ) : (
              <span className={`badge ${q.scored >= q.marks ? "pass" : q.scored > 0 ? "warn" : "fail"}`}>
                {q.scored} / {q.marks}
              </span>
            )}
          </div>
        ))}
      </div>

      <p className="muted" style={{ fontSize: 13 }}>
        A mark your teacher has changed by hand is the one shown here. If something looks wrong,
        raise it with them rather than resubmitting — an exam is sat once.
      </p>
    </div>
  );
}
